import React, { useState, useEffect, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApplicationUIContext } from '@yuhuinc/ui-library';

import { Row, Col, Button, Space, Card, Divider, Typography, Modal, Form, Alert } from 'antd';
import { useWizard } from 'components/shared/Wizard';
import { getTranslatedString } from 'helpers/i18n';
import { apiGet, apiPost } from 'helpers/ApiHelper';
import ApplicationSidebar from 'components/tenant_app/application_portal/application_sidebar/ApplicationSidebar';
import PaymentBlock from './components/PaymentBlock';
import PaymentDetails from './components/PaymentDetails';

const { Title } = Typography;

const DepositPayment = ({ applicationTemplate, cotenantId, isPreview }) => {
  const { previousStep, nextStep } = useWizard();
  const { isMobile } = useContext(ApplicationUIContext);

  const [loading, setLoading] = useState(false);
  const [paysafeInstance, setPaysafeInstance] = useState();
  const [cardNumberError, setCardNumberError] = useState('');
  const [cvvError, setCvvError] = useState('');
  const [expiryDateError, setExpiryDateError] = useState('');
  const [paymentError, setPaymentError] = useState();
  const [paymentForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: cotenant } = useQuery({
    queryKey: ['cotenant', cotenantId],
    retry: false,
    staleTime: 120000,
    queryFn: () =>
      apiGet(`/leads/application_portal/cotenants/${cotenantId}`).then((res) => res.cotenant),
  });

  const leasePricing = cotenant?.lease?.lease_pricing;
  const paymentDisabled =
    !leasePricing?.payment_details?.split_pay || leasePricing?.payment_details?.required;

  useEffect(() => {
    if (paymentDisabled) {
      paymentForm.setFieldsValue({ payment_amount: leasePricing?.deposit_owing });
    }
  }, [paymentDisabled]);

  const paymentSuccessfulModal = () => {
    const increaseButtonSizeOnMobile = { size: isMobile ? 'large' : 'default' };

    Modal.success({
      title: 'Payment successful',
      content: 'An email has been sent to you confirming receipt of payment.',
      onOk: () => {
        nextStep();
      },
      okText: getTranslatedString('okay'),
      okButtonProps: increaseButtonSizeOnMobile,
      bodyStyle: { fontSize: isMobile ? 16 : 14 },
    });
  };

  const handleSubmit = async () => {
    if (isPreview) return;
    const paymentDetails = await paymentForm.validateFields();

    try {
      if (!paysafeInstance) {
        setPaymentError('Issue processing payment');
        window.Rollbar.error('No paysafe instance');
        throw Error;
      }

      setLoading(true);

      paysafeInstance.tokenize((instance, error, result) => {
        if (error) {
          if (error.fieldErrors) {
            this.handlePaysafeFieldErrorsOnSubmit(error.fieldErrors);
          } else {
            setPaymentError('Your payment was not successful. Please check your card information');
          }
          setLoading(false);
          window.Rollbar.error('Tokenization error: ' + error.code + ' ' + error.detailedMessage);
        } else {
          console.log({ token: result.token, payment_details: paymentDetails });
          apiPost(`/leads/application_portal/cotenants/${cotenantId}/payments/make_cc_payment`, {
            token: result.token,
            payment_amount: paymentDetails['payment_amount'],
            name: paymentDetails['name'],
            postal_code: paymentDetails['postal_code'],
          })
            .then((res) => {
              console.log(res);
              setLoading(false);
              queryClient.invalidateQueries({ queryKey: ['cotenant', cotenantId] });
              paymentSuccessfulModal();
            })
            .catch((e) => {
              setPaymentError(
                'Your payment was not successful. Please check your card information'
              );
              setLoading(false);
              console.log(e);
            });
        }
      });
    } catch (error) {
      setPaymentError('Your payment was not successful. Please check your card information');
      setLoading(false);
    }
  };

  return (
    <Row gutter={16}>
      <Col className="gutter-row" xs={{ span: 24, order: 0 }} md={{ span: 8, order: 1 }}>
        <ApplicationSidebar applicationWizard cotenantId={cotenantId} isPreview={isPreview} />
      </Col>

      <Col xs={24} md={0}>
        <Divider />
      </Col>

      <Col className="gutter-row" xs={{ span: 24, order: 1 }} md={{ span: 16, order: 0 }}>
        <Card>
          <Title level={4}>{applicationTemplate.deposit_payment_title}</Title>
          <div
            dangerouslySetInnerHTML={{ __html: applicationTemplate?.deposit_payment_description }}
          ></div>
          {paymentError && <Alert message={paymentError} type="error" showIcon />}
          <Divider />
          <PaymentDetails leasePricing={leasePricing} />
          <PaymentBlock
            form={paymentForm}
            setPaysafeInstance={setPaysafeInstance}
            cardNumberError={cardNumberError}
            cvvError={cvvError}
            expiryDateError={expiryDateError}
            setCardNumberError={setCardNumberError}
            setCvvError={setCvvError}
            setExpiryDateError={setExpiryDateError}
            paymentDisabled={paymentDisabled}
            paymentHold={leasePricing?.payment_details?.hold}
          />
          <Divider />
          <Space>
            <Button loading={loading} type="primary" onClick={handleSubmit}>
              Pay now
            </Button>
            <Button
              type="default"
              onClick={nextStep}
              disabled={leasePricing?.payment_details?.required}
            >
              Skip
            </Button>
            <Button type="default" onClick={previousStep}>
              {getTranslatedString('back')}
            </Button>
          </Space>
        </Card>
      </Col>
    </Row>
  );
};

export default DepositPayment;
